import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import LoginButton from './LoginButton';

const PlatformPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const endpoints = [
    {
      method: 'GET',
      path: '/x402/requirements',
      desc: 'Discover payee address and accepted payment schemes for a customer + amount.',
      methodCls: 'text-cyan-400 bg-cyan-950/60 border-cyan-800/30',
    },
    {
      method: 'POST',
      path: '/x402/payments',
      desc: 'Submit a signed EIP-712 authorization. ZeroPay verifies and settles on-chain.',
      methodCls: 'text-emerald-400 bg-emerald-950/60 border-emerald-800/30',
    },
    {
      method: 'GET',
      path: '/x402/support',
      desc: 'List supported payment schemes and networks on this facilitator instance.',
      methodCls: 'text-cyan-400 bg-cyan-950/60 border-cyan-800/30',
    },
    {
      method: 'GET',
      path: '/x402/discovery',
      desc: 'Browse payment-enabled resources. Agents use this to find services autonomously.',
      methodCls: 'text-cyan-400 bg-cyan-950/60 border-cyan-800/30',
    },
  ];

  const platformFeatures = [
    {
      title: 'x402 Protocol Ready',
      desc: 'Full implementation of the x402 Agent-to-Agent payment standard. Any compliant AI agent can pay without configuration.',
      detail: ['EIP-3009 gasless transfers', 'EIP-712 typed signatures', 'EIP-8004 agent identity', 'x402/v1 compliant'],
    },
    {
      title: 'Privacy-Auditable',
      desc: 'Every settlement is on-chain verifiable. No off-chain data stored. Audit any payment without trusting a third party.',
      detail: ['On-chain settlement proof', 'No custody of funds', 'Transparent commission', 'Open-source code'],
    },
    {
      title: 'Zero Infrastructure',
      desc: 'The managed platform handles PostgreSQL, Redis, RPC nodes, and chain scanning. You bring your wallet and API key.',
      detail: ['Managed DB + Redis', 'Multiple EVM chains', 'Automatic failover', 'Docker-equivalent setup'],
    },
    {
      title: 'Developer First',
      desc: 'Four REST endpoints. No SDK required. If your agent can sign EIP-712 and make HTTP calls, it can pay.',
      detail: ['Four endpoints total', 'No SDK required', 'API key auth', 'Full API docs on GitHub'],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1120] via-[#0D1628] to-[#080D1A]">

      {/* ─── Navigation ─── */}
      <nav className="bg-[#0B1120]/80 backdrop-blur-lg border-b border-white/[0.06] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-nobg.png" alt="ZPayNow" className="w-7 h-7" />
              <span className="font-mono text-[13px] text-gray-400">
                zpaynow<span className="text-gray-700 mx-0.5">/</span><span className="text-cyan-500">platform</span>
              </span>
            </Link>
            <div className="flex items-center gap-3">
              <Link
                to="/"
                className="text-gray-500 hover:text-white px-3 py-1.5 rounded-md text-[12px] font-mono transition-colors hover:bg-white/5"
              >
                ← home
              </Link>
              <a
                href="https://github.com/zpaynow/ZeroPay/blob/main/API.md"
                target="_blank" rel="noopener noreferrer"
                className="text-gray-500 hover:text-white px-3 py-1.5 rounded-md text-[12px] font-mono transition-colors hover:bg-white/5"
              >
                docs
              </a>
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="bg-cyan-500 hover:bg-cyan-400 text-[#080D1A] px-5 py-2 rounded-lg text-[12px] font-mono font-bold transition-all shadow-lg shadow-cyan-500/20"
                >
                  Dashboard →
                </Link>
              ) : (
                <LoginButton size="small" />
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <div className="relative overflow-hidden py-20 sm:py-28">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/[0.04] rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="max-w-2xl">
            <div className="inline-block mb-6 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-md">
              <span className="text-cyan-400 text-[11px] font-mono uppercase tracking-wider">Managed Platform</span>
            </div>
            <h1 className="text-5xl sm:text-6xl font-black text-white mb-6 leading-[0.95] tracking-tight">
              ZeroPay<br />
              <span className="text-cyan-400">as a Service.</span>
            </h1>
            <p className="text-[1.05rem] text-gray-400 mb-4 leading-[1.75]">
              The full x402 facilitator stack — managed. Same open-source protocol, same API, zero infrastructure overhead.
            </p>
            <p className="text-[0.9rem] text-gray-600 mb-10 font-mono">
              api.zpaynow.com · x402/v1 · EIP-3009 · GPL-3.0 source
            </p>

            <div className="flex flex-wrap gap-4">
              {!isAuthenticated && (
                <LoginButton size="large" />
              )}
              {isAuthenticated && (
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[#080D1A] px-8 py-3.5 rounded-xl font-mono font-bold text-sm transition-all shadow-lg shadow-cyan-500/20"
                >
                  Go to Dashboard →
                </Link>
              )}
              <a
                href="https://github.com/zpaynow/ZeroPay"
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 border border-white/10 hover:border-white/20 text-gray-400 hover:text-white px-8 py-3.5 rounded-xl font-mono text-sm transition-all"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                View Source
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ─── API Endpoints ─── */}
      <div className="border-t border-white/[0.06] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-[10px] font-mono text-gray-600 uppercase tracking-[0.18em] mb-3">
              API Surface
            </div>
            <h2 className="text-[1.75rem] font-black text-white">Four endpoints. That's it.</h2>
          </div>

          <div className="border border-white/[0.05] rounded-xl overflow-hidden divide-y divide-white/[0.05]">
            {endpoints.map((ep, i) => (
              <div
                key={i}
                className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 bg-[#0B1120] hover:bg-white/[0.015] transition-colors"
              >
                <div className="flex items-center gap-4 flex-shrink-0">
                  <span className={`text-[11px] font-mono px-2.5 py-1 rounded border ${ep.methodCls} w-[42px] text-center`}>
                    {ep.method}
                  </span>
                  <code className="text-[13px] font-mono text-white sm:w-52">{ep.path}</code>
                </div>
                <p className="text-[13px] text-gray-500 leading-[1.6] sm:border-l sm:border-white/[0.05] sm:pl-6">
                  {ep.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[12px] font-mono text-gray-600">
              Base URL: <span className="text-gray-400">https://api.zpaynow.com</span>
              <span className="mx-3 text-gray-700">·</span>
              Auth: <span className="text-gray-400">?apikey=your-key</span>
            </span>
          </div>
        </div>
      </div>

      {/* ─── Platform Features ─── */}
      <div className="border-t border-white/[0.06] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12">
            <div className="text-[10px] font-mono text-gray-600 uppercase tracking-[0.18em] mb-3">
              Platform Capabilities
            </div>
            <h2 className="text-[1.75rem] font-black text-white">What's included.</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-px bg-white/[0.05] rounded-xl overflow-hidden border border-white/[0.05]">
            {platformFeatures.map((f, i) => (
              <div key={i} className="bg-[#0B1120] p-7 hover:bg-white/[0.02] transition-colors">
                <h3 className="text-[15px] font-bold text-white mb-2">{f.title}</h3>
                <p className="text-[13px] text-gray-500 leading-[1.7] mb-5">{f.desc}</p>
                <ul className="space-y-1.5">
                  {f.detail.map((d) => (
                    <li key={d} className="flex items-center gap-2.5 text-[12px] font-mono text-gray-600">
                      <svg className="w-3.5 h-3.5 text-cyan-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── CTA ─── */}
      <div className="border-t border-white/[0.06] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="relative rounded-2xl border border-white/[0.07] overflow-hidden p-12 sm:p-16 text-center bg-gradient-to-br from-cyan-950/[0.15] via-transparent to-indigo-950/[0.15]">
            <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/[0.04] to-transparent pointer-events-none" />

            <div className="relative z-10">
              <div className="text-[10px] font-mono text-cyan-600 uppercase tracking-[0.18em] mb-5">
                Sign in with Ethereum
              </div>
              <h2 className="text-[2rem] font-black text-white mb-4">
                Ready to start?
              </h2>
              <p className="text-gray-500 mb-10 max-w-lg mx-auto text-[0.95rem] leading-[1.8]">
                Connect your wallet. Get an API key. Your agents can start paying via x402 in minutes.
                Full source available on <a href="https://github.com/zpaynow/ZeroPay" className="text-gray-300 hover:text-white underline underline-offset-2" target="_blank" rel="noopener noreferrer">GitHub</a>.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                {isAuthenticated ? (
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2 px-8 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-[14px] font-mono font-bold text-[#080D1A] transition-all shadow-lg shadow-cyan-500/20"
                  >
                    Go to Dashboard →
                  </Link>
                ) : (
                  <LoginButton size="large" />
                )}
                <a
                  href="https://github.com/zpaynow/ZeroPay/blob/main/docs/AI_INTEGRATION_GUIDE.md"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-8 py-3 rounded-xl border border-white/10 hover:border-white/20 text-[14px] font-mono text-gray-400 hover:text-white transition-all"
                >
                  AI Integration Guide →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformPage;
